import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { MaterialDetail } from "@shared-core/data/manufacturing-setup-types";
import { formatSyncedFrom } from "@shared-core/domain/manufacturing-setup-presentation";
import { formatCurrencyGbp } from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { SettingsBreadcrumbs } from "../app/components/patterns/SettingsBreadcrumbs";
import { Badge } from "../app/components/primitives/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";
import { EngineUseFieldTable } from "../app/components/domain/manufacturing/EngineUseFieldTable";

/** The breadcrumb trail shared by every state of this page — Settings ›
 *  Catalogue › Live costing › Manufacturing setup, plus this material's own
 *  identity once it has loaded. */
function crumbs(leaf?: string) {
  const base = [
    { label: "Settings", to: paths.settingsIntegrations() },
    { label: "Catalogue", to: paths.settingsCatalogues() },
    { label: "Live costing", to: paths.settingsLiveCostingStatus() },
  ];
  return leaf
    ? [
        ...base,
        {
          label: "Manufacturing setup",
          to: paths.settingsLiveCostingManufacturingSetup(),
        },
        { label: leaf },
      ]
    : [...base, { label: "Manufacturing setup" }];
}

export function ManufacturingSetupMaterialDetailPage() {
  const adapter = useDataAdapter();
  const { code } = useParams<{ code: string }>();

  const [detail, setDetail] = useState<MaterialDetail | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(() => {
    if (!code) return;
    setLoadError(false);
    adapter
      .getManufacturingMaterial(code)
      .then((d) => {
        if (d === null) {
          setNotFound(true);
          return;
        }
        setDetail(d);
      })
      .catch(() => setLoadError(true));
  }, [adapter, code]);

  useEffect(() => {
    load();
  }, [load]);

  if (!code || notFound) {
    return (
      <BreadcrumbsOnly>
        <p className="text-muted-foreground">
          No such material. The link may be out of date.
        </p>
      </BreadcrumbsOnly>
    );
  }

  if (loadError) {
    return (
      <BreadcrumbsOnly>
        <p className="text-destructive">
          Couldn&rsquo;t load this material. Please try again.
        </p>
      </BreadcrumbsOnly>
    );
  }

  if (!detail) {
    return (
      <BreadcrumbsOnly>
        <p className="text-muted-foreground">Loading...</p>
      </BreadcrumbsOnly>
    );
  }

  return (
    <div className="space-y-6">
      <SettingsBreadcrumbs crumbs={crumbs(detail.code)} />

      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-foreground">
          {detail.code}
        </h1>
        <p className="text-sm text-muted-foreground">
          {detail.gsmOrMicrons} {detail.gsmOrMicronsUnit}
          {/* Calliper is already the headline figure for a microns-specified
              material — repeating it here would say the same number twice. */}
          {detail.gsmOrMicronsUnit === "gsm" &&
            detail.calliperMicrons != null && (
              <> · {detail.calliperMicrons} microns calliper</>
            )}
        </p>
        <p className="text-xs text-muted-foreground">
          {formatSyncedFrom(detail.sync)}
        </p>
      </div>

      <div className="space-y-2">
        <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Fields
        </h2>
        <EngineUseFieldTable
          fields={detail.fields}
          emptyMessage="No fields captured for this material."
        />
      </div>

      <div className="space-y-2">
        <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Sheet sizes
        </h2>
        {detail.sheetSizes.length === 0 ? (
          <p className="rounded-md border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
            No sheet sizes captured for this material.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Width × depth</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Pack quantity</TableHead>
                  <TableHead>Full pack</TableHead>
                  <TableHead>Availability</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detail.sheetSizes.map((s, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-medium tabular-nums">
                      {s.widthMm} × {s.depthMm} mm
                    </TableCell>
                    <TableCell className="text-muted-foreground tabular-nums">
                      {s.pricePence > 0
                        ? `${formatCurrencyGbp(s.pricePence / 100, 2)} ${s.pricingType}`
                        : "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground tabular-nums">
                      {s.packQuantity}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {s.useFullPack ? "Full pack only" : "Any quantity"}
                    </TableCell>
                    <TableCell>
                      <Badge variant={s.isAvailable ? "success" : "neutral"}>
                        {s.isAvailable ? "Available" : "Unavailable"}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}

function BreadcrumbsOnly({ children }: { children: React.ReactNode }) {
  return (
    <div className="space-y-4">
      <SettingsBreadcrumbs crumbs={crumbs()} />
      {children}
    </div>
  );
}
